import type { SessionService } from '@efdigital/ll-addon-auth';
import { debounce } from '@efdigital/ll-addon-ui';
import {
  buildSearchUsersByText,
  type UserInfo,
} from '@efdigital/ll-admin-v2/data/builders/system/users';
import type UserLookupService from '@efdigital/ll-admin-v2/services/user-lookup';
import { userDisplayName } from '@efdigital/ll-admin-v2/utils/users';
import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { htmlSafe } from '@ember/template';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { Await } from '@warp-drive/ember';
import { cell, resource, use } from 'ember-resources';

export type AugmentedSuggestion<T> = {
  user: UserInfo;
  meta: T;
  disabled: boolean;
};

export type UserPickerSignature<T> = {
  Args: {
    inputId: string;
    userId?: string | null;
    setUserId: (userId: string | null) => void;
    clientId?: string;
    label?: string;
    /** Drop the card border/padding — for sitting inline in a filter bar. */
    compact?: boolean;
  };
  Element: HTMLDivElement;
  Blocks: {
    before?: [];
    after?: [];
    suggestion: [AugmentedSuggestion<T>];
  };
};

type SuggestionsState<T> = {
  loading: boolean;
  suggestions: AugmentedSuggestion<T>[];
};

export abstract class BaseUserPicker<T = undefined> extends Component<
  UserPickerSignature<T>
> {
  @service declare userLookup: UserLookupService;
  @service declare session: SessionService;
  @tracked searchText = '';
  @tracked isEditing = this.args.userId ? false : true;

  setSearchText = (event: Event) => {
    this.searchText = (event.target as HTMLInputElement).value;
  };

  get label() {
    return this.args.label ?? 'Search for a user';
  }

  get wrapperClass(): string {
    return this.args.compact ? '' : 'border rounded-2 p-3';
  }

  /**
   * Resolves the display text for an already-selected id. Override where the
   * central user directory isn't a reliable source of a name — e.g. coach
   * accounts, whose title/email it doesn't carry — to resolve from wherever
   * that value actually lives instead.
   */
  resolveSelectedDisplay = async (userId: string): Promise<string> => {
    const user = await this.userLookup.lookupUser(userId);
    return userDisplayName(user);
  };

  /** Override this to augment the suggestions with additional data. */
  abstract augmentSuggestions(
    suggestions: UserInfo[]
  ): Promise<AugmentedSuggestion<T>[]>;

  /** Override this to filter the suggestions before displaying them. */
  abstract filterSuggestions(
    suggestions: AugmentedSuggestion<T>[]
  ): AugmentedSuggestion<T>[];

  #getSuggestions = async (): Promise<AugmentedSuggestion<T>[]> => {
    const trimmed = this.searchText?.trim();
    if (trimmed.length < 3) {
      return Promise.resolve([]);
    }

    const request = await buildSearchUsersByText(
      {
        text: trimmed,
        clientId: this.args.clientId,
      },
      this.session
    );

    const response = await fetch(request);
    if (!response.ok) {
      console.error(`Failed to get suggestions: ${response.statusText}`);
      return [];
    }

    const users = (await response.json()) as UserInfo[];
    const augmented = await this.augmentSuggestions(users);
    return this.filterSuggestions(augmented);
  };

  @use suggestions = resource<SuggestionsState<T>>(() => {
    const state = cell<SuggestionsState<T>>({ loading: true, suggestions: [] });

    if (!this.isEditing) {
      return {
        loading: false,
        suggestions: [],
      };
    }

    this.#getSuggestions()
      .then((users) => {
        state.set({ loading: false, suggestions: users });
      })
      .catch((error) => {
        console.error(`Failed to get suggestions: ${error}`);
        state.set({ loading: false, suggestions: [] });
      });

    return () => state.current;
  });

  setUserId = (userId: string) => {
    this.args.setUserId(userId);
    this.searchText = '';
    this.isEditing = false;
  };

  startEditing = () => {
    this.isEditing = true;
    this.searchText = '';

    // eslint-disable-next-line ember/no-runloop
    next(() => {
      const input = document.getElementById(
        this.args.inputId
      ) as HTMLInputElement;
      if (input) {
        input.focus();
      }
    });
  };

  stopEditing = () => {
    this.isEditing = false;
    this.searchText = '';
  };

  clearSelection = () => {
    this.args.setUserId(null);
    this.searchText = '';
    this.isEditing = true;
  };

  get suggestionsStyle() {
    return htmlSafe('max-height: 400px;');
  }

  get showInput() {
    return this.isEditing || this.args.userId;
  }

  <template>
    <div class={{this.wrapperClass}} ...attributes>
      {{yield to="before"}}
      <div class="hstack gap-2">
        {{#if this.showInput}}
          <div class="hstack position-relative flex-grow-1">
            {{#if this.isEditing}}
              <div class="input-group">
                <span class="input-group-text">
                  <span class="visually-hidden">Search</span>
                  <iconify-icon
                    icon="ph:magnifying-glass"
                    class="pe-none"
                    inline
                    aria-hidden="true"
                  />
                </span>
                <input
                  id={{@inputId}}
                  type="text"
                  value={{this.searchText}}
                  class="form-control"
                  placeholder="Search by name or email"
                  required
                  {{debounce this.setSearchText}}
                />
              </div>
            {{else if @userId}}
              <Await @promise={{this.resolveSelectedDisplay @userId}}>
                <:success as |name|>
                  <input
                    id={{@inputId}}
                    type="text"
                    value={{name}}
                    class="form-control"
                    disabled
                    readonly
                    {{debounce this.setSearchText}}
                  />
                </:success>
                <:pending>
                  {{! template-lint-disable no-duplicate-id }}
                  <input
                    id={{@inputId}}
                    type="text"
                    value="..."
                    class="form-control"
                    disabled
                    readonly
                    {{debounce this.setSearchText}}
                  />
                </:pending>
              </Await>
            {{/if}}
            {{#if this.suggestions.loading}}
              <div
                class="spinner-border spinner-border-sm text-body-secondary position-absolute end-0 me-3 z-5"
                role="status"
              >
              </div>
            {{/if}}
          </div>
        {{/if}}
        {{#if this.isEditing}}
          {{#if @userId}}
            <button
              class="btn btn-outline-primary btn-sm"
              type="button"
              {{on "click" this.stopEditing}}
            >
              Cancel
            </button>
          {{/if}}
        {{else}}
          <button
            class="btn btn-outline-primary btn-sm"
            type="button"
            {{on "click" this.startEditing}}
          >
            <iconify-icon
              icon="ph:pencil-simple"
              class="pe-none"
              inline
              aria-hidden="true"
            />
            Change user
          </button>
          {{#if @userId}}
            <button
              class="btn btn-outline-secondary btn-sm"
              type="button"
              {{on "click" this.clearSelection}}
            >
              Clear
            </button>
          {{/if}}
        {{/if}}
      </div>

      {{#if this.suggestions.suggestions.length}}
        <div
          class="shadow overflow-scroll rounded-4 position-absolute"
          style={{this.suggestionsStyle}}
        >
          <div class="list-group">
            {{#each this.suggestions.suggestions as |suggestion|}}
              <button
                class="list-group-item list-group-item-action"
                type="button"
                disabled={{suggestion.disabled}}
                {{on "click" (fn this.setUserId suggestion.user.id)}}
              >{{#if (has-block "suggestion")}}
                  {{yield suggestion to="suggestion"}}
                {{else}}
                  {{suggestion.user.title}}
                {{/if}}
              </button>
            {{/each}}
          </div>
        </div>
      {{/if}}
      {{yield to="after"}}
    </div>
  </template>
}

export class UserPicker extends BaseUserPicker<undefined> {
  augmentSuggestions(
    suggestions: UserInfo[]
  ): Promise<AugmentedSuggestion<undefined>[]> {
    return Promise.resolve(
      suggestions.map((suggestion) => ({
        user: suggestion,
        meta: undefined,
        disabled: false,
      }))
    );
  }

  filterSuggestions(
    suggestions: AugmentedSuggestion<undefined>[]
  ): AugmentedSuggestion<undefined>[] {
    return suggestions;
  }
}
